/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { isNil } from 'lodash';
import { TerminalDetails } from '../shared/entities/terminal';
import {
  AL_MAP_VERSION_PREFIX,
  ContourType,
  darkThemeBeamRGBValues, LATEST,
  lightThemeBeamRGBValues, MapType, Network, VIHNA_MAP_VERSION_PREFIX
} from '../shared/entities/beam';
import { bpsToUnitString } from './DataRateUtils';
import { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import difference from '@turf/difference';
import { coordAll } from '@turf/meta';
import { Color } from '@deck.gl/core';

const HCX_LOWER_CASE = 'hcx';
const GX_LOWER_CASE = 'gx';

export enum BEAM_TYPE {
  KU = 'Ku',
  GX = 'GX',
  KA = 'Ka',
  HCX = 'HCX',
  OFFLINE = 'Offline',
  UNKNOWN = 'Unknown',
}

/**
 * Get the beam type from the beam and network origin.
 * @param beamValue the value of the beam as a string
 * @param networkOrigin the network origin associated with the beam
 */
export const getBeamTypeFromValue = (beamValue?: string | null, networkOrigin?: string | null): BEAM_TYPE => {
  let beamType = BEAM_TYPE.KA;

  if (isNil(networkOrigin) && isNil(beamType)) {
    beamType = BEAM_TYPE.OFFLINE;
  }
  // handle the case when the terminal is online but the beam is unknown
  else if (!isNil(networkOrigin) && isNil(beamValue)) {
    beamType = BEAM_TYPE.UNKNOWN;
  } else if (!isNil(beamValue) && beamValue.toLowerCase().indexOf(HCX_LOWER_CASE) !== -1) {
    beamType = BEAM_TYPE.HCX;
  } else if (!isNil(beamValue) && beamValue.toLowerCase().indexOf(GX_LOWER_CASE) !== -1) {
    beamType = BEAM_TYPE.GX;
  } else if (!isNil(beamValue) && isNaN(Number(beamValue))) {
    // Note: beam names for Ka are numbers and Ku have a combination of numbers and characters
    beamType = BEAM_TYPE.KU;
  }

  return beamType;
};

/**
 * This function will check a terminal to determine whether or not to use
 * the singular Viasat beam overlay, split Viasat beam overlays, Kasat beam
 * overlay, the NBNCO beam overlay name, or the HYLAS beam overlay name.
 *
 * @param terminal terminal details
 * @param viasatBeamOverlayName name of the viasat network's beam overlay
 */
export const getKaBeamOverlayName = (terminal: TerminalDetails, viasatBeamOverlayName?: string) => {
  const { beamName, viasatBeamName, networkOrigin } = terminal;
  if (getBeamTypeFromValue(beamName, networkOrigin) === BEAM_TYPE.KA) {
    // If the beams endpoint returns one of the split beams, use the split beam name.
    // O/w use the viasat beam overlay name that was chosen from the beams endpoint.
    return viasatBeamName || viasatBeamOverlayName || 'VIASAT';
  } else {
    return '';
  }
};

export const darkTheme = {
  fillColor: (feature: any) => getDefaultColorFromFeature(feature, darkThemeBeamRGBValues, 71),
  lineColor: (feature: any) => getDefaultColorFromFeature(feature, darkThemeBeamRGBValues, 103),
  highlightColor: (feature: any) => getDefaultColorFromFeature(feature, darkThemeBeamRGBValues, 255),
  beamHighlightColor: (feature: any) => getDefaultColorFromFeature(feature, darkThemeBeamRGBValues, 255),
};

export const lightTheme = {
  fillColor: (feature: any) => getDefaultColorFromFeature(feature, lightThemeBeamRGBValues, 71),
  lineColor: (feature: any) => getDefaultColorFromFeature(feature, lightThemeBeamRGBValues, 103),
  highlightColor: (feature: any) => getDefaultColorFromFeature(feature, lightThemeBeamRGBValues, 255),
  beamHighlightColor: (feature: any) => getDefaultColorFromFeature(feature, lightThemeBeamRGBValues, 255),
};

/**
 * Helper function to get feature properties, handling both regular and highlighted objects
 */
const getFeatureProperties = (feature: any) => {
  const properties = feature.properties || feature.object?.properties;
  return {
    mapType: properties?.mapType,
    networkType: properties?.network,
    groundTxAllowed: properties?.groundTxAllowed,
    hnaAltitude: properties?.hnaAltitude,
  };
};

/**
 * Helper function to determine HNA color based on altitude and ground transmission status
 */
const getHnaColor = (groundTxAllowed: boolean | undefined, hnaAltitude: number | undefined, themedRGBValues: any) => {
  if (groundTxAllowed === false && hnaAltitude === undefined) {
    return themedRGBValues.hnaWeightOnWheels;
  }
  if (hnaAltitude === 99999) {
    return themedRGBValues.hnaAllAirspaceAltitude;
  }
  return themedRGBValues.hnaAtAltitude;
};

/**
 * Helper function to get coverage color based on network type
 */
const getCoverageColor = (networkType: string | undefined, themedRGBValues: any) => {
  const networkColorMap: Record<string, any> = {
    [Network.VGNET]: themedRGBValues.ku,
    [Network.I5_HCX]: themedRGBValues.hcx,
    [Network.VIASAT]: themedRGBValues.ka,
    [Network.VIASAT_3]: themedRGBValues.VIASAT_3,
    [Network.GX]: themedRGBValues.gx,
    [Network.RTE]: themedRGBValues.rte,
  };
  return networkColorMap[networkType as string] || themedRGBValues.ku;
};

/**
 * Gets the color from the GeoJSON feature's properties.
 * @param feature the feature that deck.gl will pass to the function in order to get the color.
 * @param opacity the opacity to set for the color in rgba format 0 <= opacity <= 255
 */
export const getDefaultColorFromFeature = (feature: any, themedRGBValues = darkThemeBeamRGBValues, opacity = 255) => {
  const hasDataRate = (feature.properties || {}).rlDataRateBps || (feature.properties || {}).flDataRateBps;
  const { mapType, networkType, groundTxAllowed, hnaAltitude } = getFeatureProperties(feature);

  let rgbValues = themedRGBValues.ku;

  if (mapType === MapType.COVERAGE) {
    rgbValues = getCoverageColor(networkType, themedRGBValues);
  } else if (mapType === MapType.FL_HEAT_MAP || mapType === MapType.RL_HEAT_MAP) {
    rgbValues = themedRGBValues.kuHeatmap;
  } else if (mapType === MapType.HNA) {
    rgbValues = getHnaColor(groundTxAllowed, hnaAltitude, themedRGBValues);
  }

  return hasDataRate ? rgbValues : [...rgbValues.slice(0, 3), opacity];
};

export const getTooltipTextWithHnaAltitude = (feature: any) => {
  let tooltipText = feature?.properties.displayName;

  if (feature?.properties.mapType === MapType.HNA) {
    if (feature?.properties?.groundTxAllowed === false && feature?.properties?.hnaAltitude === undefined) {
      tooltipText = `${tooltipText} - Weight on Wheels`;
    } else if (feature?.properties?.hnaAltitude === 99999) {
      tooltipText = `${tooltipText} - All Airspace`;
    } else {
      tooltipText = `${tooltipText} - <=${feature?.properties.hnaAltitude.toLocaleString()} ft`;
    }
  }

  return tooltipText;
};

export const getTooltipTextWithDataRate = (beamName: string, dataRateBps: number) =>
  `${beamName} - ${bpsToUnitString(dataRateBps)}`;

// Custom algorithm for finding the extreme North, South, East, West coordinates from polygons that can span the antimeridian since
// turfjs (and other libraries) cannot handle the antmerdian with Features that span the anti meridian
export const getExtremesFromGeometry = (feature: any) => {
  let indexOfMostNorth = 0;
  let greatestLat = -91;
  let indexOfMostSouth = 0;
  let lowestLat = 91;
  let indexOfMostEast = 0;
  let greatestLon = -181;
  let indexOfMostWest = 0;
  let lowestLon = 181;
  const allCoords = coordAll(feature);
  allCoords.forEach((coordinate: number[], index: number) => {
    const thisLon = coordinate[0];
    const thisLat = coordinate[1];
    if (thisLat > greatestLat) {
      greatestLat = thisLat;
      indexOfMostNorth = index;
    }
    if (thisLat < lowestLat) {
      lowestLat = thisLat;
      indexOfMostSouth = index;
    }
    if (thisLon > greatestLon) {
      greatestLon = thisLon;
      indexOfMostEast = index;
    }
    if (thisLon < lowestLon) {
      lowestLon = thisLon;
      indexOfMostWest = index;
    }
  });

  const north = allCoords[indexOfMostNorth];
  const south = allCoords[indexOfMostSouth];
  let east = allCoords[indexOfMostEast];
  let west = allCoords[indexOfMostWest];

  // If it splits the anti meridian, the furthest west point will be in the easter hemisphere and the furthest east point will be in the western hemisphere
  if (west[0] === -180 && east[0] === 180) {
    // Reset the longitude calculations
    indexOfMostEast = 0;
    greatestLon = -181;
    indexOfMostWest = 0;
    lowestLon = 181;

    // Iterate again
    allCoords.forEach((coordinate: number[], index: number) => {
      const thisLon = coordinate[0];

      // The furthest east point will be amongst coordinates with negative longitudes
      if (thisLon > greatestLon && thisLon < 0) {
        greatestLon = thisLon;
        indexOfMostEast = index;
      }

      // The furthest west point will be amongst coordinates with positive longitudes
      if (thisLon < lowestLon && thisLon > 0) {
        lowestLon = thisLon;
        indexOfMostWest = index;
      }
    });

    east = allCoords[indexOfMostEast];
    west = allCoords[indexOfMostWest];
  }
  return { north, east, south, west };
};

/**
  * Perform a cutout of the feature colleciton features by taking the difference of the polygons from the first index to the last.
  * 
  * difference(polygon1, polygon2);  // returns polygon1 with a cutout of the intersection with polygon2
 
     1) [feature1, feature2, feature3, feature4]
 
     2) [feature1, difference(feature2, feature1), difference(feature3, feature1), difference(feature4, feature1)]
 
     3) [feature1, difference(feature2, feature1), difference(difference(feature3, feature1), feature2), difference(difference(feature4, feature1), feature2)]
 
     4) [feature1, difference(feature2, feature1), difference(difference(feature3, feature1), feature2), difference(difference(difference(feature4, feature1), feature2), feature3)]
 
  * @param featureCollection the feature collection to perform the cutout for
  * @param sortFunc the sort function that will put the polygons with the highest priority first in the list
  * @param disableBeamCutoutFunc a function that determines if a cutout should be done based on the second polygon which will be cutting out the first polygon
  */
export const performCutout = (
  featureCollection: FeatureCollection<Polygon | MultiPolygon>,
  sortFunc?: (feature1: Feature<Polygon | MultiPolygon>, feature2: Feature<Polygon | MultiPolygon>) => number,
  disableBeamCutoutFunc?: (
    feature1: Feature<Polygon | MultiPolygon>,
    feature2: Feature<Polygon | MultiPolygon>,
  ) => boolean,
): FeatureCollection<Polygon | MultiPolygon> => {
  let data = { ...featureCollection, features: featureCollection.features.slice() };

  if (sortFunc) {
    data.features.sort(sortFunc);
  }
  if (data.features.length > 1) {
    for (let outerIdx = 0; outerIdx < data.features.length - 1; outerIdx += 1) {
      for (let innerIdx = outerIdx + 1; innerIdx < data.features.length; innerIdx += 1) {
        let featureToPerformCut = data.features[outerIdx];
        let featureToCut = data.features[innerIdx];

        if (!disableBeamCutoutFunc || !disableBeamCutoutFunc(featureToCut, featureToPerformCut)) {
          const diff = difference(featureToCut, featureToPerformCut);

          if (!diff) {
            // the feature has the same contour as another feature or has been completely cutout
            data.features.splice(innerIdx, 1);

            // change inner index to accomdate the removal of the feature
            innerIdx -= 1;
          } else {
            data.features[innerIdx] = diff;
          }
        }
      }
    }
  }

  return data;
};

/**
 * Disables the cutting of beams with the same name and beams that are HNA zones.
 * @param feature1 the feature that will be cut
 * @param feature2 the feature that will cut the first feature
 * @returns true if the cut should not be performed on these two features
 */
export const disableBeamCutoutForHnaAndSameBeam = (
  feature1: Feature<Polygon | MultiPolygon>,
  feature2: Feature<Polygon | MultiPolygon>,
): boolean => {
  const name1 = feature1.properties?.displayName
    ? feature1.properties.displayName
    : feature1.properties?.beamDisplayName;
  const name2 = feature2.properties?.displayName
    ? feature2.properties.displayName
    : feature2.properties?.beamDisplayName;

  return (
    feature1.properties?.mapType === MapType.HNA ||
    feature2.properties?.mapType === MapType.HNA ||
    name1 === name2
  );
};

/**
 * Helper function to disable the beam hover for all contours besides VGNET.
 * @param networkType the networkType of the contour
 * @param mapType the mapType of the contour 
 * @returns true to disable beam hover for all contours except VGNET when it returns false.
 */
export const disableBeamHoverForAllContoursBesidesVGNET = (networkType: Network, mapType: MapType): boolean => {
  const targetedMapTypes = [MapType.COVERAGE, MapType.FL_HEAT_MAP, MapType.RL_HEAT_MAP]
  if (networkType === Network.VGNET && targetedMapTypes.includes(mapType))
    return false;
  else
    return true;
};

/**
 * Helper function to disable the beam contour hover for VGNET.
 * @param networkType
 * @param mapType
 * @returns true to disable beam contour hover for VGNET and false for all other contours.
 */
export const disableBeamContourHoverForVGNET = (networkType: Network, mapType: MapType): boolean => {
  const targetedMapTypes = [MapType.COVERAGE, MapType.FL_HEAT_MAP, MapType.RL_HEAT_MAP]
  if (networkType === Network.VGNET && targetedMapTypes.includes(mapType))
    return true;
  else
    return false;
};

// only used in unit tests (move to BeamUtils)
export const hexToRgb = (hex: string) => {
  let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

// Only used in unit tests
export const rgbaFromHex = (hex: string, opacity = 255): Color => {
  const hexAsRgb = hexToRgb(hex);
  const rgbAArray: Color = hexAsRgb ? [hexAsRgb.r, hexAsRgb.g, hexAsRgb.b, opacity] : [0, 0, 0, opacity];
  return rgbAArray;
};

// Only used in unit tests
export const getColorFromDb = (name: string, opacity = 255): Color => {
  if (!name) {
    return [255, 255, 255, 255];
  }

  const dbWithK = name.split('G/T:')[1];
  const dbRaw = dbWithK.split('dB/K')[0];
  const db = parseInt(dbRaw);
  let hex = '#FFFFFF';
  if (db > 6) {
    hex = '#FF0000';
  } else if (db > 0) {
    hex = '#ff782b';
  } else {
    hex = '#f5ff48';
  }
  return rgbaFromHex(hex, opacity);
};

// only used in unit tests (move to BeamUtils)
export const dataRateFromName = (name: string): string => {
  if (!name) {
    return 'N/A';
  }
  const dbWithK = name.split('G/T:')[1];
  const dbRaw = dbWithK.split('dB/K')[0];
  const db = parseInt(dbRaw);
  if (db > 6) {
    return '1024 kbps';
  } else if (db > 0) {
    return '512 kbps';
  } else {
    return '64 kbps';
  }
};

// only used in unit tests (move to BeamUtils)
export const getNorthCoordinate = (coordinates: any[]) => {
  let indexOfMostNorth = 0;
  let greatestLat = -91;
  coordinates.forEach((coordinate: any[], index: number) => {
    const thisLat = coordinate[1];
    if (thisLat > greatestLat) {
      greatestLat = thisLat;
      indexOfMostNorth = index;
    }
  });
  return coordinates[indexOfMostNorth];
};

// only used in unit tests (move to BeamUtils)
export const getSouthCoordinate = (coordinates: any[]) => {
  let indexOfMostSouth = 0;
  let lowestLat = 91;
  coordinates.forEach((coordinate: any[], index: number) => {
    const thisLat = coordinate[1];
    if (thisLat < lowestLat) {
      lowestLat = thisLat;
      indexOfMostSouth = index;
    }
  });
  return coordinates[indexOfMostSouth];
};

// only used in unit tests (move to BeamUtils)
export const getEastCoordinate = (coordinates: any[]) => {
  let indexOfMostEast = 0;
  let greatestLon = -181;
  coordinates.forEach((coordinate: any[], index: number) => {
    const thisLon = coordinate[0];
    if (thisLon > greatestLon) {
      greatestLon = thisLon;
      indexOfMostEast = index;
    }
  });
  return coordinates[indexOfMostEast];
};

// only used in unit tests (move to BeamUtils)
export const getWestCoordinate = (coordinates: any[]) => {
  let indexOfMostWest = 0;
  let greatestLon = 181;
  coordinates.forEach((coordinate: any[], index: number) => {
    const thisLon = coordinate[0];
    if (thisLon < greatestLon) {
      greatestLon = thisLon;
      indexOfMostWest = index;
    }
  });
  return coordinates[indexOfMostWest];
};

/**
 * Helper function to get ArcLight map version string based on antenna type ID and SSCF file version
 * It will always return the latest version if the SSCF file version is not provided because the latest version is the default
 * @param antennaTypeId
 * @param sscfFileVersion
 * @returns map version string
 */
export const getArcLightMapVersion = (antennaTypeId: number, sscfFileVersion: string | undefined | null) : string => {
  return `${AL_MAP_VERSION_PREFIX}${sscfFileVersion || LATEST}-${antennaTypeId}`;
};

/**
 * Helper function to get VI HNA map version string based on VI HNA policy version
 * It will always return empty version if the VI HNA policy version is not provided
 * @param viHnaPolicyVersion
 * @returns map version string
 */
export const getViHnaMapVersion = (viHnaPolicyVersion: string | null | undefined ) : string => {
  return viHnaPolicyVersion ? `${VIHNA_MAP_VERSION_PREFIX}${viHnaPolicyVersion}` : '';
};

/**
 * Temporarily used helper function to get networkType from contourType for service alerts API response
 * WARNING: Assume KA_HNA -> Viasat. Need to be modified when adding Viasat_3 networks to MPT
 * TODO: remove when network field is added to service alert response.
 * 
 * @param contourType
 * @returns networkType
 */
export const getServiceAlertNetworkType = (contourType: string) : Network => {
  switch(contourType as ContourType) {
    case ContourType.VGNET:
    case ContourType.HNA:
      return Network.VGNET
    case ContourType.HCX:
    case ContourType.HCX_HNA:
      return Network.I5_HCX
    case ContourType.VIASAT:
    case ContourType.KA_HNA:
      return Network.VIASAT
    case ContourType.VIASAT_3:
      return Network.VIASAT_3
    case ContourType.GX:
    case ContourType.GX_HNA:
      return Network.GX
    default:
      return Network.OTHER
  }
}

/**
 * Temporarily used helper function to get mapType from contourType
 * TODO: remove when mapType is added to service alerts response
 * @param contourType
 * @returns mapType
 */
export const getServiceAlertMapType = (contourType: string) : MapType | undefined => {
  switch(contourType as ContourType) { // TODO: assume non HNA == COVERAGE maps
    case ContourType.VGNET:
    case ContourType.HCX:
    case ContourType.VIASAT:
    case ContourType.VIASAT_3:
    case ContourType.GX:
      return MapType.COVERAGE
    case ContourType.HNA:
    case ContourType.HCX_HNA:
    case ContourType.KA_HNA:
    case ContourType.GX_HNA:
      return MapType.HNA
    default:
      return undefined
  }
}

/**
 * Helper function to get contourType based on network & map types
 * @param networkType
 * @param mapType
 * @returns contourType
 */
export const getContourType = (networkType: string, mapType: string) : string => {
  switch (mapType as MapType) {
    case MapType.COVERAGE:
    case MapType.FL_HEAT_MAP:
    case MapType.RL_HEAT_MAP:
      switch (networkType as Network){
        case Network.VGNET:
          return ContourType.VGNET
        case Network.I5_HCX:
          return ContourType.HCX
        case Network.VIASAT:
          return ContourType.VIASAT
        case Network.VIASAT_3:
          return ContourType.VIASAT_3
        case Network.GX:
          return ContourType.GX
        default:
          return ContourType.OTHER
      }
    case MapType.HNA:
      switch (networkType as Network){
        case Network.VGNET:
          return ContourType.HNA
        case Network.I5_HCX:
          return ContourType.HCX_HNA
        case Network.VIASAT:
        case Network.VIASAT_3:
          return ContourType.KA_HNA
        case Network.GX:
          return ContourType.GX_HNA
        default:
          return ContourType.OTHER
      }
    default:
      return ContourType.OTHER
  }
};
